import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageMeta from "./PageMeta";
import {
  fishingMarks,
  confirmedFishingMarkAreas,
  confirmedFishingMarkSpecies,
} from "./fishingMarks.ts";

const holidayFaqs = [
  {
    q: "Where can I fish in Cornwall on holiday?",
    a: "This page lists shore fishing marks all around Cornwall — including popular holiday bases like Newquay, St Ives, Falmouth and Padstow — covering beaches, rocks, harbours, piers and estuaries. Use the area, venue type or species filters above to find marks near where you're staying.",
  },
  {
    q: "Do you need a licence to sea fish in Cornwall?",
    a: "No. An Environment Agency rod licence is only required for freshwater and migratory fish (e.g. rivers and lakes). Shore and boat sea fishing around the Cornish coast does not require a rod licence.",
  },
  {
    q: "Do I need to be a club member to fish these marks?",
    a: "No, these are public shore fishing marks that any visiting angler can fish. Redruth Sea Angling Association shares them to help holidaymakers and visiting anglers find good, safe places to fish in Cornwall.",
  },
  {
    q: "What fish can you catch from the shore in Cornwall?",
    a: "Depending on the mark, season and tide, shore anglers commonly catch bass, pollock, wrasse, mackerel, rays, bull huss and gilthead bream. Each mark below lists its typical target species, best season, methods and baits.",
  },
  {
    q: "What's the best time of year to fish in Cornwall?",
    a: "Cornwall fishes year-round, but many marks and species peak through the spring, summer and autumn holiday season. Check the \"Best season\" note on each mark, and see our fishing conditions and tide pages before you head out.",
  },
  {
    q: "Is it safe to fish from Cornwall's rocks and headlands?",
    a: "Some marks carry safety notes for swell, tide cut-off or slippery access — always check these first, watch the tide, and read our angler safety advice before fishing anywhere unfamiliar.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: holidayFaqs.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

export default function FishingMarksPage() {
  const navigate = useNavigate();
  const [area, setArea] = useState("All areas");
  const [type, setType] = useState("All venue types");
  const [species, setSpecies] = useState("All species");
  const [search, setSearch] = useState("");

  const venueTypes = useMemo(
    () => Array.from(new Set(fishingMarks.map((mark) => mark.type))).sort(),
    [],
  );

  const filteredMarks = useMemo(() => {
    const query = search.trim().toLowerCase();
    return fishingMarks.filter((mark) => {
      const matchesArea = area === "All areas" || mark.area === area;
      const matchesType = type === "All venue types" || mark.type === type;
      const matchesSpecies = species === "All species" || mark.species.includes(species);
      const matchesSearch = !query || `${mark.name} ${mark.area} ${mark.description}`.toLowerCase().includes(query);
      return matchesArea && matchesType && matchesSpecies && matchesSearch;
    });
  }, [area, search, species, type]);

  const googleMapsUrl = (mark) => {
    const query = mark.coordinates
      ? `${mark.coordinates.lat},${mark.coordinates.lng}`
      : `${mark.name}, ${mark.area}, Cornwall`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  };

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta
        title="Places to Fish in Cornwall | Cornwall Fishing Marks | Redruth Sea Angling Association"
        description="Places to fish in Cornwall for visiting anglers — discover Cornwall shore fishing marks including beaches, rocks, headlands, harbours, piers and estuaries. Find bass, pollock, wrasse, mackerel, rays, bull huss and gilthead bream venues with Google Maps links."
        keywords="Places to fish in Cornwall, places to fish Cornwall, best places to fish in Cornwall, where to fish in Cornwall, Cornwall fishing marks, Cornwall shore fishing, sea fishing Cornwall, visiting anglers Cornwall, bass fishing Cornwall, pollock fishing Cornwall, wrasse fishing Cornwall, mackerel fishing Cornwall, ray fishing Cornwall, bull huss Cornwall, gilthead bream Cornwall, Helford River fishing, Falmouth fishing marks, Lizard fishing marks, West Cornwall fishing, North Cornwall fishing, Newquay fishing marks, St Ives fishing marks, rock fishing Cornwall, beach fishing Cornwall, pier fishing Cornwall, estuary fishing Cornwall, Redruth Sea Angling Association"
        url="https://www.redruthseaanglingassociation.co.uk/fishing-marks"
      />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <main className="mx-auto max-w-7xl px-4 pb-16 pt-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="standalone-only group inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-black/20 backdrop-blur transition hover:-translate-x-0.5 hover:border-cyan-300/40 hover:bg-cyan-400/10 hover:text-white"
          >
            ← Back
          </button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Cornwall shore fishing</p>
            <h1 className="heading-glow text-2xl font-bold text-white sm:text-3xl">Places to Fish in Cornwall</h1>
          </div>
        </div>

        <section className="mb-8 border-b border-white/10 pb-8">
          <p className="max-w-3xl text-base leading-7 text-slate-300">
            Planning a fishing trip on holiday in Cornwall? Browse our listed shore fishing marks by location, venue type or species to find
            somewhere to fish near Newquay, St Ives, Falmouth, Padstow and all along the Cornish coast — no club membership needed.
            For up-to-date conditions before you head out, check our{" "}
            <Link to="/fishing-conditions" className="text-cyan-300 underline decoration-cyan-400/40 underline-offset-2 hover:text-cyan-200">
              tides and fishing conditions
            </Link>{" "}
            and{" "}
            <Link to="/angler-safety" className="text-cyan-300 underline decoration-cyan-400/40 underline-offset-2 hover:text-cyan-200">
              angler safety advice
            </Link>
            , or find bait and tackle nearby on our{" "}
            <Link to="/tackle-shops" className="text-cyan-300 underline decoration-cyan-400/40 underline-offset-2 hover:text-cyan-200">
              local tackle shops
            </Link>{" "}
            page.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="sm:col-span-2 lg:col-span-1">
              <span className="sr-only">Search fishing marks</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search marks"
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition focus:border-cyan-400/60"
              />
            </label>
            <FilterSelect label="Area" allLabel="All areas" value={area} onChange={setArea} options={confirmedFishingMarkAreas} />
            <FilterSelect label="Venue type" allLabel="All venue types" value={type} onChange={setType} options={venueTypes} />
            <FilterSelect label="Species" allLabel="All species" value={species} onChange={setSpecies} options={confirmedFishingMarkSpecies} />
          </div>
        </section>

        <div className="mb-4 flex items-center justify-between gap-4">
          <p className="text-sm text-slate-400">
            Showing <span className="font-semibold text-white">{filteredMarks.length}</span> of {fishingMarks.length} listed marks
          </p>
          <button
            type="button"
            onClick={() => { setArea("All areas"); setType("All venue types"); setSpecies("All species"); setSearch(""); }}
            className="text-sm font-semibold text-cyan-300 transition hover:text-cyan-200"
          >
            Clear filters
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredMarks.map((mark) => (
            <article key={mark.id} className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-cyan-400/40 hover:bg-cyan-400/[0.06] panel-lift">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">{mark.area}</p>
                  <h2 className="mt-1 text-xl font-semibold text-white">{mark.name}</h2>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <span className="rounded-full border border-white/10 px-2.5 py-1 text-xs text-slate-300">{mark.type}</span>
                  {mark.provisional && <span className="rounded-full border border-amber-300/30 bg-amber-300/10 px-2.5 py-1 text-xs text-amber-200">Needs local review</span>}
                </div>
              </div>
              <p className="mt-4 flex-1 text-sm leading-6 text-slate-300">{mark.description}</p>
              {mark.bestSeason && <p className="mt-4 text-sm text-slate-400"><strong className="text-slate-200">Best season:</strong> {mark.bestSeason}</p>}
              {mark.targetSpecies?.length > 0 && <p className="mt-3 text-sm text-slate-400"><strong className="text-slate-200">Target:</strong> {mark.targetSpecies.join(" · ")}</p>}
              {mark.methods?.length > 0 && <p className="mt-3 text-sm text-slate-400"><strong className="text-slate-200">Methods:</strong> {mark.methods.join(" · ")}</p>}
              {mark.baits?.length > 0 && <p className="mt-3 text-sm text-slate-400"><strong className="text-slate-200">Baits:</strong> {mark.baits.join(" · ")}</p>}
              <a
                href={googleMapsUrl(mark)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex w-fit items-center gap-2 rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-3 py-2 text-sm font-semibold text-cyan-200 transition hover:border-cyan-300/60 hover:bg-cyan-400/20"
              >
                <span aria-hidden="true">↗</span>
                {mark.coordinates ? "Open exact location" : "Find on Google Maps"}
              </a>
              <div className="mt-4 border-t border-white/10 pt-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Species</p>
                <p className="mt-2 text-sm leading-6 text-slate-300">{mark.species.join(" · ")}</p>
              </div>
              {mark.safetyNotes?.length > 0 && (
                <div className="mt-4 rounded-xl border border-amber-300/20 bg-amber-300/[0.06] p-3 text-sm text-amber-100">
                  <strong>Safety:</strong> {mark.safetyNotes.join(" ")}
                </div>
              )}
            </article>
          ))}
        </div>

        {filteredMarks.length === 0 && (
          <p className="border border-dashed border-white/15 px-6 py-12 text-center text-slate-400">No confirmed marks match those filters.</p>
        )}

        <section className="mt-16 border-t border-white/10 pt-10">
          <h2 className="text-xl font-semibold text-white sm:text-2xl">Fishing in Cornwall on holiday — FAQs</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {holidayFaqs.map(({ q, a }) => (
              <div key={q}>
                <h3 className="text-base font-semibold text-cyan-200">{q}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-300">{a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function FilterSelect({ label, allLabel, value, onChange, options }) {
  return (
    <label>
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400/60"
      >
        <option>{allLabel}</option>
        {options.map((option) => <option key={option}>{option}</option>)}
      </select>
    </label>
  );
}